import type { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid'
import { CustomColumnGridConfig } from '../types'
import dayjs from 'dayjs'
import { renderAlertWithMutedIndication } from '@/utils/mutedAlertRenderer'
import NotificationChannelStatuses from '@/components/notification-send/NotificationChannelStatuses'
import CrmStatus from '@/components/Table/CrmStatus'
import type { AlertState, CrmChip } from '@/services/notificationService'

export const CONSUMPTION_FIELD = 'consumption' as const

// At 1024 x 768 the page leaves this grid 639 px beside the messages panel.
// These minimums keep all six columns in view there. A date keeps one line;
// other cells wrap and the row grows.
const NARROW_MIN_WIDTH = 96
const DATE_MIN_WIDTH = 118

const ALERT_STATE_LABELS: Record<AlertState, string> = {
  open: 'פתוח',
  closed: 'סגור',
}

const CRM_CHIP_LABELS: Record<CrmChip, string> = {
  in_progress: 'בתהליך',
  open: 'פתוח',
}

const dateOnly = ({ value }: GridRenderCellParams) =>
  value ? dayjs(value).format('DD/MM/YYYY') : '-'

const columnsConfig: CustomColumnGridConfig[] = [
  {
    field: 'alertTypeName',
    headerName: 'התראה',
    flex: 1,
    minWidth: NARROW_MIN_WIDTH,
    fitHeader: false,
    filter: {
      type: 'options',
    },
    hideable: true,
    disableAggregation: true,
    filterable: true,
    renderCell: (params: any) =>
      renderAlertWithMutedIndication(
        params.row.alertTypeName ?? '-',
        params.row
      ),
  },
  {
    flex: 1,
    minWidth: DATE_MIN_WIDTH,
    fitHeader: false,
    field: 'openedAt',
    headerName: 'ת.פתיחה',
    hideable: true,
    disableAggregation: true,
    filter: {
      type: 'date',
    },
    filterable: true,
    renderCell: dateOnly,
  },
  {
    flex: 1,
    minWidth: DATE_MIN_WIDTH,
    fitHeader: false,
    field: 'closedAt',
    headerName: 'ת.סגירה',
    hideable: true,
    disableAggregation: true,
    filter: {
      type: 'date',
    },
    filterable: true,
    renderCell: dateOnly,
  },
  {
    field: 'channels',
    headerName: 'נשלח',
    flex: 1,
    minWidth: NARROW_MIN_WIDTH,
    filter: {
      type: 'none',
    },
    renderCell: ({ row }: GridRenderCellParams) => (
      <NotificationChannelStatuses plain channels={row.channels ?? []} />
    ),
  },
  {
    flex: 1,
    minWidth: NARROW_MIN_WIDTH,
    fitHeader: false,
    field: 'alertState',
    headerName: 'סטטוס',
    hideable: true,
    disableAggregation: true,
    filter: {
      type: 'options',
    },
    filterable: true,
    renderCell: ({
      value,
    }: GridRenderCellParams<GridValidRowModel, AlertState>) =>
      value ? ALERT_STATE_LABELS[value] : '-',
  },
  {
    flex: 1,
    minWidth: NARROW_MIN_WIDTH,
    fitHeader: false,
    field: 'crmStatus',
    headerName: 'CRM',
    hideable: true,
    disableAggregation: true,
    filter: {
      type: 'none',
    },
    renderCell: ({
      value,
    }: GridRenderCellParams<GridValidRowModel, CrmChip | null>) =>
      // An empty fragment, not null: the grid puts "-" in a cell that renders null.
      value ? (
        <CrmStatus value={value} label={CRM_CHIP_LABELS[value]} />
      ) : (
        <></>
      ),
  },
]

export default columnsConfig
